import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { SeasonProvider } from './context/SeasonContext'
import ProtectedRoute from './components/ProtectedRoute'
import { Suspense, lazy } from 'react'

// Pages publiques — chargées immédiatement
import Home from './pages/Home'
import Login from './pages/Login'
import Signup from './pages/Signup'

// Pages dashboard — imports directs (pas lazy)
// Raison : le lazy provoquait un remount du layout à chaque navigation,
// causant clignotement du logo + clics perdus.
// Bundle initial un peu plus lourd, mais navigation dashboard instantanée.
import DashboardHome     from './pages/DashboardHome'
import DashboardMatches  from './pages/DashboardMatches'
import PlayerManagement  from './pages/PlayerManagement'
import ClubSettings      from './pages/ClubSettings'
import CoachSettings     from './pages/CoachSettings'
import MonProfilPublic   from './pages/MonProfilPublic'
import MatchDetail       from './pages/MatchDetail'
import Statistics        from './pages/Statistics'
import TeamManagement    from './pages/TeamManagement'
import ClubMembers       from './pages/ClubMembers'
import CreateMatch       from './pages/CreateMatch'
import PlayerProfile     from './pages/PlayerProfile'
import Insightboard      from './pages/TacticalPad'
import SeanceDetail      from './pages/SeanceDetail'
import SeancesPage       from './pages/SeancesPage'
import Benchmark         from './pages/Benchmark'
import Onboarding        from './pages/Onboarding'
import VideoStudio       from './pages/VideoStudio'
import ProcedeDetail     from './pages/ProcedeDetail'
import ProcedeEdit       from './pages/ProcedeEdit'
import ProcedeNew        from './pages/ProcedeNew'

// Pages occasionnelles ou publiques — lazy OK (peu de navigation entre elles)
const JoinClub          = lazy(() => import('./pages/JoinClub'))
const ResetPassword     = lazy(() => import('./pages/ResetPassword'))
const RecoverAccount    = lazy(() => import('./pages/RecoverAccount'))
const SubscriptionPlans = lazy(() => import('./pages/SubscriptionPlans'))
const AdminPanel        = lazy(() => import('./pages/AdminPanel'))
const AdminCosts        = lazy(() => import('./pages/AdminCosts'))
const ClubInvite        = lazy(() => import('./pages/ClubInvite'))
const CoachInvite       = lazy(() => import('./pages/CoachInvite'))
const PublicPlayerCard  = lazy(() => import('./pages/PublicPlayerCard'))
const SeancePublique    = lazy(() => import('./pages/SeancePublique'))
const CoachProfile      = lazy(() => import('./pages/CoachProfile'))
const APropos           = lazy(() => import('./pages/APropos'))
const Tarifs            = lazy(() => import('./pages/Tarifs'))
const RapportPublic     = lazy(() => import('./pages/RapportPublic'))

// Pages légales
const MentionsLegales          = lazy(() => import('./pages/MentionsLegales'))
const PolitiqueConfidentialite = lazy(() => import('./pages/PolitiqueConfidentialite'))
const PolitiqueCookies         = lazy(() => import('./pages/PolitiqueCookies'))
const CGV                      = lazy(() => import('./pages/CGV'))

const PageLoader = () => (
  <div style={{
    minHeight: '100vh', background: '#0a0908',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <span style={{
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 10, letterSpacing: '.2em',
      textTransform: 'uppercase', color: 'rgba(79,142,247,0.5)',
    }}>Chargement...</span>
  </div>
)

// Route settings — ClubSettings réservé au DS Admin d'un club.
// Critère : plan club ET role ADMIN. Le role seul ne suffit pas — un coach
// solo a aussi role ADMIN par défaut (colonne DB), c'est le plan club qui
// lève l'ambiguïté. Tout autre cas (coach solo, membre de club non-admin)
// → CoachSettings, ses réglages perso.
function SettingsRoute() {
  const { user } = useAuth()
  if (!user) return <Navigate to="/login" replace />
  const isClubPlan = user.plan === 'CLUB' || user.plan === 'CLUB_PRO'
  const isAdmin    = String(user.role || '').toUpperCase() === 'ADMIN'
  return (isClubPlan && isAdmin) ? <ClubSettings /> : <CoachSettings />
}

// Catch-all pour URLs invalides
// Comportement : loggé → /dashboard (l'utilisateur retombe sur ses pieds),
// non loggé → /login. Évite l'écran noir des URLs tronquées (ex. /matches/:id
// au lieu de /dashboard/matches/:id) et préserve l'expérience pour les coaches
// qui copient/collent des liens partagés.
function NotFoundRedirect() {
  const { user, loading } = useAuth()
  if (loading) return <PageLoader />
  return <Navigate to={user ? '/dashboard' : '/login'} replace />
}

// Helper pour les redirects legacy avec param dynamique (ex: /:id).
// Construit l'URL cible via la fonction buildTo({...params}).
function LegacyRedirect({ buildTo }) {
  const params = useParams()
  return <Navigate to={buildTo(params)} replace />
}

function App() {
  return (
    <AuthProvider>
      <SeasonProvider>
        <Router>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Landing */}
              <Route path="/" element={<Home />} />

              {/* Auth — URLs publiques */}
              <Route path="/login"  element={<Login />} />
              <Route path="/signup" element={<Signup />} />

              {/* Onboarding */}
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

              {/* Invitation club (publique — pas de auth) */}
              <Route path="/club-invite/:token" element={<ClubInvite />} />
              <Route path="/coach-invite/:token" element={<CoachInvite />} />

              {/* Carte joueur publique — partage WhatsApp (PR #20, sans auth) */}
              <Route path="/j/:token" element={<PublicPlayerCard />} />
              {/* Seance video partagee · lecture publique d'une selection de clips */}
              <Route path="/seance/:token" element={<SeancePublique />} />
              {/* Fiche publique coach · sans auth, rend son propre 404 */}
              <Route path="/coach/:slug" element={<CoachProfile />} />

              {/* Invitation & récupération */}
              <Route path="/join"           element={<JoinClub />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/recover"        element={<RecoverAccount />} />

              {/* Rapport public */}
              <Route path="/rapport-42" element={<RapportPublic />} />

              {/* Plans / Admin */}
              <Route path="/plans"  element={<SubscriptionPlans />} />
              <Route path="/admin"  element={<AdminPanel />} />
              {/* Tracker de marge — route cachée, guard superadmin côté backend */}
              <Route path="/admin/costs" element={<AdminCosts />} />

              {/* Pages légales */}
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/confidentialite"  element={<PolitiqueConfidentialite />} />
              <Route path="/cookies"          element={<PolitiqueCookies />} />
              <Route path="/cgv"              element={<CGV />} />

              {/* À propos */}
              <Route path="/a-propos" element={<APropos />} />

              {/* Tarifs */}
              <Route path="/tarifs" element={<Tarifs />} />

{/* Dashboard protégé */}
              <Route path="/dashboard"                               element={<ProtectedRoute><DashboardHome /></ProtectedRoute>} />
              <Route path="/dashboard/matches"                       element={<ProtectedRoute><DashboardMatches /></ProtectedRoute>} />
              <Route path="/dashboard/matches/new"                   element={<ProtectedRoute><CreateMatch /></ProtectedRoute>} />
              <Route path="/dashboard/matches/:matchId/video"        element={<ProtectedRoute><VideoStudio /></ProtectedRoute>} />
              <Route path="/dashboard/library/moments"               element={<Navigate to="/dashboard/matches" replace />} />
              <Route path="/dashboard/matches/:matchId"              element={<ProtectedRoute><MatchDetail /></ProtectedRoute>} />
              <Route path="/dashboard/players"                       element={<ProtectedRoute><PlayerManagement /></ProtectedRoute>} />
              <Route path="/dashboard/player/:playerId"              element={<ProtectedRoute><PlayerProfile /></ProtectedRoute>} />
              <Route path="/dashboard/stats"                         element={<ProtectedRoute><Statistics /></ProtectedRoute>} />
              <Route path="/dashboard/benchmark"                     element={<ProtectedRoute><Benchmark /></ProtectedRoute>} />
              <Route path="/dashboard/insightboard"                  element={<ProtectedRoute><Insightboard /></ProtectedRoute>} />
              <Route path="/dashboard/calendrier"                        element={<Navigate to="/dashboard/matches" replace />} />
              <Route path="/dashboard/seances/insightlab/new"         element={<ProtectedRoute><ProcedeNew /></ProtectedRoute>} />
              <Route path="/dashboard/seances/insightlab/:id"         element={<ProtectedRoute><ProcedeDetail /></ProtectedRoute>} />
              <Route path="/dashboard/seances/insightlab/:id/edit"    element={<ProtectedRoute><ProcedeEdit /></ProtectedRoute>} />
              <Route path="/dashboard/seances/:id"                    element={<ProtectedRoute><SeanceDetail /></ProtectedRoute>} />
              <Route path="/dashboard/seances"                        element={<ProtectedRoute><SeancesPage /></ProtectedRoute>} />
              <Route path="/dashboard/team"                          element={<ProtectedRoute><TeamManagement /></ProtectedRoute>} />
              <Route path="/dashboard/members"                       element={<ProtectedRoute><ClubMembers /></ProtectedRoute>} />
              <Route path="/dashboard/settings"                      element={<ProtectedRoute><SettingsRoute /></ProtectedRoute>} />
              {/* Profil public — hors SettingsRoute : accessible a tous les roles */}
              <Route path="/dashboard/mon-profil"                    element={<ProtectedRoute><MonProfilPublic /></ProtectedRoute>} />

              <Route path="/upgrade-starter"     element={<Navigate to="/tarifs" replace />} />
              <Route path="/dashboard/presences" element={<Navigate to="/dashboard/matches" replace />} />

              {/* Catch-all : URLs invalides → redirect intelligent.
                  Évite l'écran noir sur /matches/:id (sans /dashboard/) et
                  autres URLs tronquées partagées entre coaches. */}

              {/* ── Redirects legacy URLs (refonte module Seances v2) ── */}
              {/* /dashboard/projet-de-jeu et /dashboard/catalogue : modules
                  supprimes, on renvoie vers le nouveau module unifie. */}
              <Route path="/dashboard/projet-de-jeu" element={<Navigate to="/dashboard/insightboard" replace />} />
              <Route path="/dashboard/catalogue"     element={<Navigate to="/dashboard/seances" replace />} />
              {/* Anciennes routes /dashboard/exercises/* renvoyees vers le
                  nouveau path /dashboard/seances/insightlab/* (rename C4). */}
              <Route path="/dashboard/exercises/new"     element={<Navigate to="/dashboard/seances/insightlab/new" replace />} />
              <Route path="/dashboard/exercises/:id/edit" element={<LegacyRedirect buildTo={({ id }) => `/dashboard/seances/insightlab/${id}/edit`} />} />
              <Route path="/dashboard/exercises/:id"     element={<LegacyRedirect buildTo={({ id }) => `/dashboard/seances/insightlab/${id}`} />} />

              <Route path="*" element={<NotFoundRedirect />} />
            </Routes>
          </Suspense>
        </Router>
      </SeasonProvider>
    </AuthProvider>
  )
}

export default App
